import { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Card, Stack, Container, Typography, Box, Divider,
    CircularProgress, Button, Switch, FormControlLabel, Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Page from 'src/components/Page';
import syndicationApi from 'src/utils/syndicationApi';

const RootStyle = styled(Page)(({ theme }) => ({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'flex-start',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    padding: theme.spacing(4, 0),
}));

const ContentCard = styled(Card)(({ theme }) => ({
    maxWidth: 700,
    width: '100%',
    padding: theme.spacing(4),
    borderRadius: 16,
    boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
}));

const TOGGLE_LABELS = {
    email_enabled: 'Email Notifications',
    push_enabled: 'Push Notifications',
    in_app_enabled: 'In-App Notifications',
    deal_invitation: 'Deal Invitations',
    presentation_scheduled: 'Presentation Scheduled',
    qa_opened: 'Q&A Opened',
    qa_closing_soon: 'Q&A Closing Soon',
    commitment_window_open: 'Commitment Window Open',
    commitment_closing_soon: 'Commitment Closing Soon',
    allocation_result: 'Allocation Result',
    deal_cancelled: 'Deal Cancelled',
    deal_updated: 'Deal Updated',
    post_investment_update: 'Post-Investment Updates',
    document_updated: 'Document Updated',
    deadline_extended: 'Deadline Extended',
};

const GLOBAL_TOGGLES = ['email_enabled', 'push_enabled', 'in_app_enabled'];
const EVENT_TOGGLES = Object.keys(TOGGLE_LABELS).filter((k) => !GLOBAL_TOGGLES.includes(k));

export default function InvestorSettings() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        syndicationApi.get('investor/profile/notification-settings/')
            .then((res) => setSettings(res.data?.data))
            .catch(() => setSettings(null))
            .finally(() => setLoading(false));
    }, []);

    const handleToggle = async (field) => {
        const newVal = !settings[field];
        setSettings((prev) => ({ ...prev, [field]: newVal }));
        setSaving(true);
        setMessage(null);
        try {
            await syndicationApi.patch('investor/profile/notification-settings/update/', {
                [field]: newVal,
            });
            setMessage({ type: 'success', text: 'Settings saved.' });
        } catch (err) {
            setSettings((prev) => ({ ...prev, [field]: !newVal }));
            setMessage({ type: 'error', text: 'Failed to save settings.' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <RootStyle title="Settings | Gateway X Syndication">
                <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
                    <CircularProgress sx={{ color: '#fff' }} />
                </Container>
            </RootStyle>
        );
    }

    return (
        <RootStyle title="Notification Settings | Gateway X Syndication">
            <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center' }}>
                <ContentCard>
                    <Stack spacing={3}>
                        <Typography variant="h4">Notification Settings</Typography>

                        {message && (
                            <Alert severity={message.type} onClose={() => setMessage(null)}>
                                {message.text}
                            </Alert>
                        )}

                        <Divider />

                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Global Channels
                            </Typography>
                            {GLOBAL_TOGGLES.map((key) => (
                                <FormControlLabel
                                    key={key}
                                    control={
                                        <Switch
                                            checked={!!settings?.[key]}
                                            onChange={() => handleToggle(key)}
                                            disabled={saving}
                                        />
                                    }
                                    label={TOGGLE_LABELS[key]}
                                    sx={{ display: 'block', mb: 0.5 }}
                                />
                            ))}
                        </Box>

                        <Divider />

                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Event Notifications
                            </Typography>
                            {EVENT_TOGGLES.map((key) => (
                                <FormControlLabel
                                    key={key}
                                    control={
                                        <Switch
                                            checked={!!settings?.[key]}
                                            onChange={() => handleToggle(key)}
                                            disabled={saving}
                                        />
                                    }
                                    label={TOGGLE_LABELS[key]}
                                    sx={{ display: 'block', mb: 0.5 }}
                                />
                            ))}
                        </Box>

                        <Divider />
                        <Stack direction="row" spacing={2} justifyContent="center">
                            <Button component={RouterLink} to="/investor/dashboard" variant="text" size="small">
                                Dashboard
                            </Button>
                            <Button component={RouterLink} to="/investor/profile" variant="text" size="small">
                                My Profile
                            </Button>
                        </Stack>
                    </Stack>
                </ContentCard>
            </Container>
        </RootStyle>
    );
}
